import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageSquareText, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

// Otter Assist — the ONE helper-message control (OAD-111).
//
// Used on the Assistance Offered card AND on the canonical Need Detail page. Both edit the SAME
// canonical `helper_note` on the responder row — there is no per-surface copy of the message.
// Editing or clearing is never a re-offer: it notifies nobody and changes no offer state.

export const HELPER_NOTE_MAX = 500;

/** Read-only rendering, used wherever the sender/manager reads a helper's message. */
export function HelperNoteText({ note, withdrawn, testId }) {
  if (!note) return null;
  return (
    <div className="mt-2 rounded-md border border-teal-200 bg-teal-50/70 px-2.5 py-2" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] font-[700] uppercase tracking-wider text-teal-800">
        <MessageSquareText className="h-3 w-3" />
        Their message
        {withdrawn && (
          <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-[700] tracking-normal text-muted-foreground">
            Offer withdrawn
          </span>
        )}
      </div>
      <p className="mt-1 whitespace-pre-line text-[12px] text-foreground/90">{note}</p>
    </div>
  );
}

/** The helper's own editable message. Rendered only for the member who owns the offer. */
export function HelperNoteEditor({ actionId, note, withdrawn, testIdSuffix, onSaved }) {
  const qc = useQueryClient();
  const suffix = testIdSuffix || actionId;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note || "");
  useEffect(() => { setText(note || ""); }, [note]);

  const save = useMutation({
    mutationFn: async (value) =>
      (await api.patch(`/otter-assist/offers/${actionId}/note`, { note: value || null })).data,
    onSuccess: () => {
      setEditing(false);
      toast.success("Your message has been saved — only they can see it.");
      qc.invalidateQueries({ queryKey: ["oa-assistance-offered"] });
      qc.invalidateQueries({ queryKey: ["oa-need-detail"] });
      qc.invalidateQueries({ queryKey: ["oa-help-state"] });
      if (onSaved) onSaved();
    },
    onError: (e) => toast.error(e?.response?.data?.detail === "helper_note_too_long"
      ? `Please keep your message to ${HELPER_NOTE_MAX} characters.`
      : "That didn't work — please try again."),
  });

  const over = text.length > HELPER_NOTE_MAX;

  if (!editing) {
    return (
      <div className="mt-2" data-testid={`helper-note-view-${suffix}`}>
        {note ? (
          <div className="rounded-md border border-teal-200 bg-teal-50/70 px-2.5 py-2">
            <div className="flex items-center gap-1.5 text-[10px] font-[700] uppercase tracking-wider text-teal-800">
              <MessageSquareText className="h-3 w-3" /> Your message
              {withdrawn && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-[700] tracking-normal text-muted-foreground">
                  Offer withdrawn
                </span>
              )}
            </div>
            <p className="mt-1 whitespace-pre-line text-[12px] text-foreground/90"
              data-testid={`helper-note-text-${suffix}`}>{note}</p>
          </div>
        ) : null}
        <Button type="button" size="sm" variant="ghost" className="mt-1 h-7 px-2 text-[11px]"
          onClick={() => setEditing(true)} data-testid={`helper-note-edit-${suffix}`}>
          <Pencil className="mr-1.5 h-3 w-3" />
          {note ? "Edit your message" : "Add a message"}
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-border bg-secondary/40 p-2.5"
      data-testid={`helper-note-editor-${suffix}`}>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
        placeholder="Optional — tell them how you can help. Only they will see this."
        aria-label="Your message to the person who posted this need"
        className="text-[12px]" data-testid={`helper-note-input-${suffix}`} />
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className={`text-[11px] ${over ? "text-destructive" : "text-muted-foreground"}`}
          data-testid={`helper-note-count-${suffix}`}>
          {text.length}/{HELPER_NOTE_MAX}
        </span>
        <span className="flex items-center gap-1.5">
          <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
            onClick={() => { setText(note || ""); setEditing(false); }}
            data-testid={`helper-note-cancel-${suffix}`}>Cancel</Button>
          <Button type="button" size="sm" variant="outline" disabled={save.isPending || over}
            className="h-7 text-[11px]" onClick={() => save.mutate(text)}
            data-testid={`helper-note-save-${suffix}`}>
            {save.isPending ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : null} Save
          </Button>
        </span>
      </div>
    </div>
  );
}

export default HelperNoteEditor;
